// =============================================================================
// A linha da categoria aberta no Orçado vs Realizado (pedido de 27/10)
//
// A view (077) devolve uma linha por fornecedor e mês; a grade quer uma linha
// por fornecedor com os doze meses. O que mais pesou no ano vem primeiro, e o
// "sem fornecedor" fica no fim. A linha restrita (pessoal, para quem não passa
// em ve_dado_pessoal) é uma só e não tem nome.
//
// Desde a 080 (28/10) vem junto o orçado por pessoa ou fornecedor: o das
// subcategorias ligadas a alguém. Quem está orçado e ainda não recebeu também
// ganha linha. O orçado sem pessoa ligada vira uma linha própria, no fim — mas
// só quando a categoria tem alguém ligado; sem isso, ele é o orçado da
// categoria inteira, que a linha de cima já mostra.
// =============================================================================

import type { OrcadoFornecedor, RealizadoFornecedor } from "./api.ts";

export type LinhaFornecedor = {
  chave: string;
  nome: string | null;
  restrito: boolean;
  /** Doze meses, de janeiro a dezembro. */
  realizado: bigint[];
  comprometido: bigint[];
  orcado: bigint[];
  total: bigint;
  totalOrcado: bigint;
};

/** A linha do orçado que não tem pessoa ligada. */
export const ORCADO_SEM_PESSOA = "orcado-sem-pessoa";

/** A categoria tem orçado por pessoa ou fornecedor? */
export const temOrcadoPorFornecedor = (orcado: OrcadoFornecedor[]) => orcado.some((o) => o.fornecedor_id !== null);

export function agruparPorFornecedor(linhas: RealizadoFornecedor[], orcado: OrcadoFornecedor[] = []): LinhaFornecedor[] {
  const porChave = new Map<string, LinhaFornecedor>();
  const doze = () => Array.from({ length: 12 }, () => 0n);
  const linha = (chave: string, nome: string | null, restrito: boolean) => {
    let f = porChave.get(chave);
    if (!f) {
      f = { chave, nome, restrito, realizado: doze(), comprometido: doze(), orcado: doze(), total: 0n, totalOrcado: 0n };
      porChave.set(chave, f);
    }
    return f;
  };
  // A competência é texto "AAAA-MM-01": o mês sai do texto, nunca de Date.
  const mes = (competencia: string) => Number(competencia.slice(5, 7)) - 1;

  for (const l of linhas) {
    const f = l.restrito
      ? linha("restrito", null, true)
      : linha(l.fornecedor_id ?? "sem-fornecedor", l.fornecedor_nome, false);
    const m = mes(l.competencia);
    const r = BigInt(l.realizado_centavos);
    f.realizado[m] = f.realizado[m]! + r;
    f.comprometido[m] = f.comprometido[m]! + BigInt(l.comprometido_centavos);
    f.total += r;
  }

  if (temOrcadoPorFornecedor(orcado)) {
    for (const o of orcado) {
      const f = o.fornecedor_id === null
        ? linha(ORCADO_SEM_PESSOA, null, false)
        : linha(o.fornecedor_id, o.fornecedor_nome, false);
      const m = mes(o.competencia);
      const v = BigInt(o.orcado_centavos);
      f.orcado[m] = f.orcado[m]! + v;
      f.totalOrcado += v;
    }
  }

  const fim = (f: LinhaFornecedor) => (f.chave === ORCADO_SEM_PESSOA ? 2 : f.chave === "sem-fornecedor" ? 1 : 0);
  const peso = (f: LinhaFornecedor) => (f.total > f.totalOrcado ? f.total : f.totalOrcado);
  return [...porChave.values()].sort((a, b) =>
    fim(a) - fim(b)
    || (peso(a) === peso(b) ? 0 : peso(a) > peso(b) ? -1 : 1)
    || (a.nome ?? "").localeCompare(b.nome ?? ""));
}
